import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { PointTuple } from 'leaflet';
import { type BaseMapType, type PinType } from './constants';
import { createPinIcon } from './pinUtils';
import {
  circleLngLat, fastighetWmsTileUrl, formatDistance, getDistanceRings, movedAtLeast, ringLabelPoint, ringOpacity,
  PITCH_MAX, PITCH_TILTED, smoothBearing, VITTRING_COLOR, vittringChanged, vittringCone,
} from './mapGeometry';

/**
 * 3D-terrängvy för jaktkartan (MapLibre GL). Fristående från Leaflet-kartan i KartaTab:
 * tar emot färdig data som props och ritar den ovanpå terrängen. Höjddata: AWS Terrain Tiles
 * (terrarium). Se docs/utredning-u1.md (I1) för varför detta valdes framför Googles 3D.
 * Samma lager som den platta kartan och samma geometri (karta/mapGeometry.ts).
 */

export interface Karta3DPoint { id: string; lat: number; lng: number; name: string; avatarUrl?: string | null; color?: string }
export interface Karta3DTrack { id: string; color: string; width: number; pts: [number, number][] }
export interface Karta3DPin { id: string; lat: number; lng: number; type: PinType; name: string | null; showLabel?: boolean }
export interface Karta3DVittring { origin: { lat: number; lng: number }; windDir: number; windSpd: number | null }

export interface Karta3DProps {
  center: [number, number];
  /** Den platta kartans läge just nu (läses när 3D-vyn skapas); 3D startar där och lutar sedan. */
  startView?: () => { center: [number, number]; zoom: number; bearing: number } | null;
  baseMap: BaseMapType;
  /** GeoJSON för markgränser och delområden/drev (mark.coordinatesJson), redan parsad. */
  areas: unknown | null;
  me: { lat: number; lng: number } | null;
  /** Avståndsringar runt mig med siffror; avstånden väljs efter zoom som i 2D. */
  showRings: boolean;
  /** Vittringskon (null = av eller ingen vind ännu). */
  vittring: Karta3DVittring | null;
  /** Lantmäteriets fastighetsgränser och fastighetsnamn. */
  fastighetsgranser: boolean;
  /** Kompassläge: kartan vrids mjukt efter telefonens riktning och följer min position. */
  compassActive: boolean;
  /** Senaste kompassriktningen (läses varje bildruta, så att den inte ritar om React-trädet). */
  getHeading: () => number | null;
  /** QV2: WebGL tappades (eller gick inte att starta); kartan ritas inte längre. */
  onContextLost?: () => void;
  participants: Karta3DPoint[];
  dogs: Karta3DPoint[];
  tracks: Karta3DTrack[];
  pins: Karta3DPin[];
  exaggeration?: number;
  /** Kartan när den finns (null när den tas bort) – lutningsreglaget ligger i vänstermenyn. */
  onMap?: (map: maplibregl.Map | null) => void;
  /** KA1: tryck på en deltagare (id som i participants). */
  onParticipantClick?: (id: string) => void;
  /** UX14: följningen i kompassläget är pausad (man har dragit kartan); vridningen fortsätter. */
  followPaused?: boolean;
  /** UX14: man drar kartan själv i kompassläget (start/slut) – KartaTab pausar följningen. */
  onUserPan?: (phase: 'start' | 'end') => void;
}

const DEM_TILES = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';
/**
 * Höjddatan i Sverige är inte finare än ~30 m, vilket nivå 13 (256-pixelsplattor) redan täcker;
 * högre nivåer gav bara fler plattor att hämta och avkoda under zoom (K7).
 */
const DEM_MAXZOOM = 13;
/** iPhone har devicePixelRatio 3; 2 ger 2,25 gånger färre pixlar att rita med knappt synlig skillnad (K7). */
export const MAX_PIXEL_RATIO = 2;
const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

/**
 * MapLibre tonar ner markörer bakom terrängen genom att läsa djupbufferten (gl.readPixels) för varje
 * markör var 100:e ms under rörelse. Varje avläsning tvingar CPU:n att vänta in GPU:n – med ett
 * 60-tal nålar blev det ~180 avläsningar per zoomgest och hackig zoom i WKWebView (K7). Nålar,
 * jägare och hundar ska ändå synas bakom en höjd, så avläsningen stängs av för alla markörer.
 */
maplibregl.Marker.prototype._updateOpacity = function () { /* alltid fullt synlig */ };

function baseStyle(baseMap: BaseMapType, exaggeration: number): maplibregl.StyleSpecification {
  const tiles = baseMap === 'satellite'
    ? ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}']
    : baseMap === 'terrain'
      ? ['https://tile.opentopomap.org/{z}/{x}/{y}.png']
      : ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'];
  const attribution = baseMap === 'satellite' ? '© Esri' : baseMap === 'terrain' ? '© OpenStreetMap, © OpenTopoMap' : '© OpenStreetMap';
  return {
    version: 8,
    sources: {
      base: { type: 'raster', tiles, tileSize: 256, maxzoom: baseMap === 'terrain' ? 17 : 19, attribution },
      // En DEM-källa för både terräng och skuggning: varje höjdplatta hämtas och avkodas en gång (K7).
      dem: { type: 'raster-dem', tiles: [DEM_TILES], encoding: 'terrarium', tileSize: 256, maxzoom: DEM_MAXZOOM, attribution: 'AWS Terrain Tiles' },
    },
    layers: [
      { id: 'base', type: 'raster', source: 'base' },
      { id: 'hillshade', type: 'hillshade', source: 'dem', paint: { 'hillshade-exaggeration': baseMap === 'satellite' ? 0.35 : 0.2 } },
    ],
    terrain: { source: 'dem', exaggeration },
  };
}

function areasData(areas: unknown): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  if (areas) {
    try {
      const g = areas as any;
      const list: any[] = g.type === 'FeatureCollection' ? g.features : g.type === 'Feature' ? [g] : [{ type: 'Feature', properties: {}, geometry: g }];
      for (const f of list) {
        if (!f?.geometry) continue;
        features.push({ type: 'Feature', properties: { sub: f.properties?.type === 'sub_area' }, geometry: f.geometry });
      }
    } catch { /* ogiltig GeoJSON — hoppa över */ }
  }
  return { type: 'FeatureCollection', features };
}

function tracksData(tracks: Karta3DTrack[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: tracks.filter(t => t.pts.length >= 2).map(t => ({
      type: 'Feature', properties: { color: t.color, width: t.width },
      geometry: { type: 'LineString', coordinates: t.pts.map(([la, ln]) => [ln, la]) },
    })),
  };
}

function vittringData(v: Karta3DVittring | null): GeoJSON.FeatureCollection {
  if (!v) return EMPTY;
  return {
    type: 'FeatureCollection',
    features: vittringCone(v.origin, v.windDir, v.windSpd).map(({ pts, op }) => {
      const ring = pts.map(([la, ln]) => [ln, la]);
      ring.push(ring[0]);
      return { type: 'Feature', properties: { op }, geometry: { type: 'Polygon', coordinates: [ring] } };
    }),
  };
}

function markerEl(color: string, label: string, avatarUrl?: string | null): HTMLElement {
  const el = document.createElement('div');
  el.title = label;
  el.style.cssText = `width:22px;height:22px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,.6);background:${color};color:#1a2d18;font:800 10px system-ui;display:flex;align-items:center;justify-content:center;overflow:hidden`;
  if (avatarUrl) {
    const img = document.createElement('img');
    img.src = avatarUrl;
    img.style.cssText = 'width:100%;height:100%;object-fit:cover';
    el.appendChild(img);
  } else {
    el.textContent = label.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  }
  return el;
}

/** Samma nål (och passnamn) som den platta kartan. */
function pinMarker(p: Karta3DPin): maplibregl.Marker {
  const icon = createPinIcon(p.type, p.name, p.type === 'pass' ? { passShowLabel: p.showLabel } : undefined);
  const el = document.createElement('div');
  el.innerHTML = String(icon.options.html ?? '');
  el.title = p.name || '';
  const [ax, ay] = (icon.options.iconAnchor as PointTuple | undefined) ?? [0, 0];
  return new maplibregl.Marker({ element: el, anchor: 'top-left', offset: [-ax, -ay] });
}

function ringLabelEl(): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = 'color:#D97E00;font-size:12px;font-weight:800;white-space:nowrap;text-align:center;pointer-events:none;text-shadow:0 0 2px #fff,0 0 2px #fff,0 0 3px #fff,0 0 4px rgba(255,255,255,0.9)';
  return el;
}

type MarkerStore = Map<string, { m: maplibregl.Marker; sig: string }>;

/** Lägger till, flyttar och tar bort markörer per id i stället för att bygga om alla. */
function syncMarkers(map: maplibregl.Map, store: MarkerStore, items: { key: string; lng: number; lat: number; sig: string; make: () => maplibregl.Marker }[]) {
  const keep = new Set<string>();
  for (const it of items) {
    keep.add(it.key);
    const cur = store.get(it.key);
    if (cur && cur.sig === it.sig) {
      cur.m.setLngLat([it.lng, it.lat]);
      continue;
    }
    cur?.m.remove();
    store.set(it.key, { m: it.make().setLngLat([it.lng, it.lat]).addTo(map), sig: it.sig });
  }
  for (const [k, v] of store) {
    if (!keep.has(k)) { v.m.remove(); store.delete(k); }
  }
}

export function Karta3D(props: Karta3DProps) {
  const divRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  /** Samma karta som state, så att föräldern får den (lutningsreglaget i vänstermenyn). */
  const [mapState, setMapState] = useState<maplibregl.Map | null>(null);
  const onMapRef = useRef(props.onMap);
  onMapRef.current = props.onMap;
  useEffect(() => {
    onMapRef.current?.(mapState);
    return () => onMapRef.current?.(null);
  }, [mapState]);
  const pinMarkers = useRef<MarkerStore>(new Map());
  const pointMarkers = useRef<MarkerStore>(new Map());
  const ringMarkers = useRef<maplibregl.Marker[]>([]);
  const propsRef = useRef(props);
  propsRef.current = props;
  /** Vittringen som senast skickades till kartan (konen byggs inte om för GPS-brus). */
  const vittringDrawn = useRef<Karta3DVittring | null>(null);
  const exaggeration = props.exaggeration ?? 1.4;

  const setData = (id: string, data: GeoJSON.FeatureCollection) => {
    (mapRef.current?.getSource(id) as maplibregl.GeoJSONSource | undefined)?.setData(data);
  };

  // Ringarna och deras siffror: samma avstånd som 2D vid samma utsnitt (MapLibre-zoom + 1 = Leaflet-zoom).
  // Vid ny GPS-position ritas de bara om när jag flyttat mig minst 2 m (`fromGps`), inte för varje brusig fix.
  const ringsDrawn = useRef<{ me: { lat: number; lng: number } | null; showRings: boolean } | null>(null);
  const updateRings = (fromGps = false) => {
    const map = mapRef.current;
    if (!map) return;
    const { me, showRings } = propsRef.current;
    const prev = ringsDrawn.current;
    if (fromGps && prev && prev.showRings === showRings && !movedAtLeast(prev.me, me, 2)) return;
    ringsDrawn.current = { me, showRings };
    const dists = me && showRings ? getDistanceRings(map.getZoom() + 1) : [];
    setData('rings', {
      type: 'FeatureCollection',
      features: me ? dists.map((d, i) => ({
        type: 'Feature', properties: { op: ringOpacity(i) },
        geometry: { type: 'LineString', coordinates: circleLngLat(me.lat, me.lng, d) },
      })) : [],
    });
    const labels = ringMarkers.current;
    while (labels.length > dists.length) labels.pop()!.remove();
    dists.forEach((d, i) => {
      if (!labels[i]) labels[i] = new maplibregl.Marker({ element: ringLabelEl(), anchor: 'center' }).setLngLat([0, 0]).addTo(map);
      const el = labels[i].getElement();
      const text = formatDistance(d);
      if (el.textContent !== text) el.textContent = text;
      const [la, ln] = ringLabelPoint(me!, d);
      labels[i].setLngLat([ln, la]);
    });
  };

  // Skapa kartan en gång; bytt baslager byter stil (och lägger tillbaka lagren).
  useEffect(() => {
    if (!divRef.current) return;
    const start = props.startView?.() ?? null;
    const center = start?.center ?? props.center;
    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
      container: divRef.current,
      style: baseStyle(props.baseMap, exaggeration),
      center: [center[1], center[0]],
      // MapLibre har 512-pixelsplattor: samma utsnitt som Leaflet är en zoomnivå lägre.
      zoom: start ? start.zoom - 1 : 13.5,
      pitch: 0,
      bearing: start?.bearing ?? 0,
      maxPitch: PITCH_MAX,
      pixelRatio: Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO),
      attributionControl: { compact: true },
      });
    } catch (e) {
      console.warn('[3d] kartan gick inte att starta', e);
      propsRef.current.onContextLost?.();
      return;
    }
    mapRef.current = map;
    // QV2: utan WebGL målas kartan inte, men markörerna (DOM) ligger kvar och flyttas med bäringen
    // ovanpå den platta kartan – låt föräldern visa den platta kartan i stället.
    map.on('webglcontextlost', () => {
      console.warn('[3d] WebGL tappades');
      propsRef.current.onContextLost?.();
    });
    setMapState(map);
    // Börja som den platta kartan och luta in, så att man ser att man står kvar på samma ställe.
    map.once('load', () => map.easeTo({ pitch: PITCH_TILTED, duration: 1200 }));
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true, showZoom: false }), 'top-right');
    // Under helskärmens runda knappar uppe till höger (kompass, stäng), annars täcks den.
    const topRight = map.getContainer().querySelector<HTMLElement>('.maplibregl-ctrl-top-right');
    if (topRight) topRight.style.top = 'calc(56px + env(safe-area-inset-top, 0px))';
    const addOverlays = () => {
      const p = propsRef.current;
      if (map.getSource('areas')) return;
      // Fastighetsgränser från Lantmäteriets WMS, samma lager och stil som i 2D. Källorna hämtar
      // inga plattor så länge lagren är dolda.
      const fgVis = p.fastighetsgranser ? 'visible' : 'none';
      map.addSource('fg', { type: 'raster', tiles: [fastighetWmsTileUrl('granser')], tileSize: 256, maxzoom: 19, attribution: '© Lantmäteriet' });
      map.addSource('fgText', { type: 'raster', tiles: [fastighetWmsTileUrl('text')], tileSize: 256, maxzoom: 19 });
      map.addLayer({ id: 'fg', type: 'raster', source: 'fg', layout: { visibility: fgVis }, paint: { 'raster-opacity': 0.9 } });
      map.addLayer({ id: 'fgText', type: 'raster', source: 'fgText', layout: { visibility: fgVis }, paint: { 'raster-opacity': 0.9 } });
      map.addSource('vittring', { type: 'geojson', data: vittringData(p.vittring) });
      vittringDrawn.current = p.vittring;
      map.addSource('areas', { type: 'geojson', data: areasData(p.areas) });
      map.addSource('rings', { type: 'geojson', data: EMPTY });
      map.addSource('tracks', { type: 'geojson', data: tracksData(p.tracks) });
      // 12 tunna lager med fallande opacitet ger den mjuka konen (inga CSS-filter).
      map.addLayer({ id: 'vittring', type: 'fill', source: 'vittring', paint: { 'fill-color': VITTRING_COLOR, 'fill-opacity': ['get', 'op'], 'fill-antialias': false } });
      map.addLayer({ id: 'area', type: 'line', source: 'areas', filter: ['==', ['get', 'sub'], false], paint: { 'line-color': '#0064FF', 'line-width': 3, 'line-opacity': 0.9 } });
      map.addLayer({ id: 'subarea', type: 'line', source: 'areas', filter: ['==', ['get', 'sub'], true], paint: { 'line-color': '#00A2FF', 'line-width': 2, 'line-opacity': 0.8, 'line-dasharray': [3, 2] } });
      map.addLayer({ id: 'ring', type: 'line', source: 'rings', paint: { 'line-color': '#EE8F09', 'line-width': 2, 'line-opacity': ['get', 'op'], 'line-dasharray': [8, 6] } });
      map.addLayer({ id: 'track', type: 'line', source: 'tracks', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': ['get', 'color'], 'line-width': ['get', 'width'], 'line-opacity': 0.9 } });
      updateRings();
    };
    map.on('style.load', addOverlays);
    map.on('zoomend', () => updateRings());
    // Orientering efter första ritningen: skärmen kan ha bytt storlek (helskärm).
    const ro = new ResizeObserver(() => map.resize());
    ro.observe(divRef.current);
    const pins = pinMarkers.current;
    const points = pointMarkers.current;
    return () => {
      ro.disconnect();
      pins.forEach(v => v.m.remove()); pins.clear();
      points.forEach(v => v.m.remove()); points.clear();
      ringMarkers.current.forEach(m => m.remove()); ringMarkers.current = [];
      map.remove();
      mapRef.current = null;
      setMapState(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Baslager. diff:false så att stilen laddas om helt och lagren läggs tillbaka i style.load.
  const firstBase = useRef(true);
  useEffect(() => {
    if (firstBase.current) { firstBase.current = false; return; }
    mapRef.current?.setStyle(baseStyle(props.baseMap, exaggeration), { diff: false });
  }, [props.baseMap, exaggeration]);

  useEffect(() => { setData('areas', areasData(props.areas)); }, [props.areas]);
  useEffect(() => { setData('tracks', tracksData(props.tracks)); }, [props.tracks]);
  useEffect(() => {
    if (!vittringChanged(vittringDrawn.current, props.vittring)) return;
    vittringDrawn.current = props.vittring;
    setData('vittring', vittringData(props.vittring));
  }, [props.vittring]);
  useEffect(() => updateRings(true), [props.me?.lat, props.me?.lng, props.showRings]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.getLayer('fg')) return;
    const vis = props.fastighetsgranser ? 'visible' : 'none';
    map.setLayoutProperty('fg', 'visibility', vis);
    map.setLayoutProperty('fgText', 'visibility', vis);
  }, [props.fastighetsgranser]);

  // Kompassläge: bäringen glider mot telefonens riktning varje bildruta och kartan följer min
  // position (som 2D, där kartan inte går att dra iväg). Utan kompass är vridningen fri.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !props.compassActive) return;
    // UX14: kartan går att dra även i kompassläget – då pausas följningen (som i Google Maps).
    map.dragRotate.disable();
    const onDragStart = () => propsRef.current.onUserPan?.('start');
    const onDragEnd = () => propsRef.current.onUserPan?.('end');
    map.on('dragstart', onDragStart);
    map.on('dragend', onDragEnd);
    // KB2: zooma runt mitten (= min position), så att följningen inte drar tillbaka kartan efter en nyp.
    map.scrollZoom.enable({ around: 'center' });
    map.touchZoomRotate.enable({ around: 'center' });
    map.touchZoomRotate.disableRotation();
    map.keyboard.disableRotation();
    // KB2: jumpTo stoppar pågående gester (map.stop), så följningen pausar medan man zoomar
    // och en stund efter (tröghet i nypet/hjulet).
    const container = map.getContainer();
    let touches = 0;
    let quietUntil = 0;
    const onTouch = (e: TouchEvent) => { touches = e.touches.length; quietUntil = performance.now() + 600; };
    const onWheel = () => { quietUntil = performance.now() + 500; };
    container.addEventListener('touchstart', onTouch, { capture: true, passive: true });
    container.addEventListener('touchend', onTouch, { capture: true, passive: true });
    container.addEventListener('touchcancel', onTouch, { capture: true, passive: true });
    container.addEventListener('wheel', onWheel, { capture: true, passive: true });
    let raf = 0;
    let last = performance.now();
    const tick = (t: number) => {
      raf = requestAnimationFrame(tick);
      const dt = Math.min(t - last, 100);
      last = t;
      if (map.isEasing()) return; // låt inlutningen vid start bli klar
      if (touches > 0 || t < quietUntil || map.isZooming()) return;
      const opts: maplibregl.JumpToOptions = {};
      const h = propsRef.current.getHeading();
      if (h != null) {
        const cur = (map.getBearing() + 360) % 360;
        const next = smoothBearing(cur, h, dt);
        if (next !== cur) opts.bearing = next;
      }
      const me = propsRef.current.followPaused ? null : propsRef.current.me;
      if (me) {
        const c = map.getCenter();
        if (Math.abs(c.lat - me.lat) > 1e-6 || Math.abs(c.lng - me.lng) > 1e-6) opts.center = [me.lng, me.lat];
      }
      if (opts.bearing !== undefined || opts.center) map.jumpTo(opts);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      container.removeEventListener('touchstart', onTouch, { capture: true });
      container.removeEventListener('touchend', onTouch, { capture: true });
      container.removeEventListener('touchcancel', onTouch, { capture: true });
      container.removeEventListener('wheel', onWheel, { capture: true });
      map.off('dragstart', onDragStart);
      map.off('dragend', onDragEnd);
      map.dragRotate.enable();
      map.scrollZoom.enable();
      map.touchZoomRotate.enable();
      map.touchZoomRotate.enableRotation();
      map.keyboard.enableRotation();
    };
  }, [props.compassActive]);

  // Nålar (pass, åtel, observationer, skjutet vilt, åtelkameror).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    syncMarkers(map, pinMarkers.current, props.pins.map(p => ({
      key: p.id, lng: p.lng, lat: p.lat, sig: `${p.type}|${p.name ?? ''}|${p.showLabel ? 1 : 0}`, make: () => pinMarker(p),
    })));
  }, [props.pins]);

  /** KA1: deltagaren går att trycka på (statuskortet). */
  const participantEl = (p: Karta3DPoint) => {
    const el = markerEl(p.color ?? '#EE8F09', p.name, p.avatarUrl);
    el.style.cursor = 'pointer';
    el.addEventListener('click', e => { e.stopPropagation(); propsRef.current.onParticipantClick?.(p.id); });
    return el;
  };

  // Deltagare, hundar och jag — flyttas på plats vid nya positioner.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const items = [
      // Hundens färg: orange, eller grå när en PAJ-position är gammal (H1).
      ...props.dogs.map(d => ({ key: `dog:${d.id}`, lng: d.lng, lat: d.lat, sig: `${d.name}|${d.color ?? ''}`, make: () => new maplibregl.Marker({ element: markerEl(d.color ?? '#EE8F09', d.name, null) }) })),
      ...props.participants.map(p => ({ key: `p:${p.id}`, lng: p.lng, lat: p.lat, sig: `${p.name}|${p.avatarUrl ?? ''}|${p.color ?? ''}`, make: () => new maplibregl.Marker({ element: participantEl(p) }) })),
    ];
    if (props.me) items.push({ key: 'me', lng: props.me.lng, lat: props.me.lat, sig: 'me', make: () => new maplibregl.Marker({ element: markerEl('#1e88e5', 'Jag') }) });
    syncMarkers(map, pointMarkers.current, items);
  }, [props.dogs, props.participants, props.me?.lat, props.me?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div ref={divRef} style={{ position: 'absolute', inset: 0 }} />
    </div>
  );
}
